import type {
  AdminLoginRequest,
  AdminLoginResponse,
  AdminVerifyResponse,
  PatientSummary,
  PaginatedResult,
  ListFilters,
  ChatLog,
  AnalyticsData,
  SortField,
  SortOrder,
  ChatLogSource,
  ChatLogSourceStatus,
} from '../types/admin';

const API_BASE = '/api/admin';

// Get stored token
const getToken = (): string | null => {
  return localStorage.getItem('admin_token');
};

// Shared auth headers for all admin API calls (prompts-api.ts imports this too)
export function getAdminAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Generic fetch with auth
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  // 204 No Content (e.g. PUT /agent/mode) has no body to parse
  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Auth API
export async function login(
  username: string,
  password: string
): Promise<AdminLoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password } as AdminLoginRequest),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Login failed');
  }

  const data = await response.json();

  // Store token
  localStorage.setItem('admin_token', data.token);
  localStorage.setItem('admin_username', data.username);
  localStorage.setItem('admin_expires', String(Date.now() + data.expiresIn * 1000));

  return data;
}

export async function verifyToken(): Promise<AdminVerifyResponse> {
  return fetchWithAuth<AdminVerifyResponse>('/auth/verify');
}

export function logout(): void {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_username');
  localStorage.removeItem('admin_expires');
}

export function isAuthenticated(): boolean {
  const token = getToken();
  const expires = localStorage.getItem('admin_expires');

  if (!token || !expires) return false;

  return Date.now() < parseInt(expires, 10);
}

export function getStoredUsername(): string | null {
  return localStorage.getItem('admin_username');
}

// Patient API
export async function getPatients(
  page = 1,
  limit = 20,
  sortBy: SortField = 'lastChatTime',
  sortOrder: SortOrder = 'desc',
  filters?: ListFilters
): Promise<PaginatedResult<PatientSummary>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortOrder,
  });

  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  if (filters?.search) params.append('search', filters.search);

  return fetchWithAuth<PaginatedResult<PatientSummary>>(`/patients?${params}`);
}

// Conversation API
export async function getConversation(
  patientId: string,
  sessionId: string
): Promise<ChatLog> {
  return fetchWithAuth<ChatLog>(`/conversations/${encodeURIComponent(patientId)}/${encodeURIComponent(sessionId)}`);
}

// Analytics API
export async function getAnalytics(
  dateFrom?: string,
  dateTo?: string
): Promise<AnalyticsData> {
  const params = new URLSearchParams();
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);

  const queryString = params.toString();
  return fetchWithAuth<AnalyticsData>(`/analytics${queryString ? `?${queryString}` : ''}`);
}

// Export API
export async function exportData(
  format: 'csv' | 'json' = 'json',
  patientId?: string,
  filters?: ListFilters
): Promise<Blob> {
  const token = getToken();

  const params = new URLSearchParams({ format });
  if (patientId) params.append('patientId', patientId);
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);

  const response = await fetch(`${API_BASE}/export?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  return response.blob();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Conversation source & deletion API
export const getChatLogSource = () => fetchWithAuth<ChatLogSourceStatus>(`/chat-log-source`);

export const setChatLogSource = (source: ChatLogSource) =>
  fetchWithAuth<{ source: ChatLogSource }>(`/chat-log-source`, {
    method: 'PUT',
    body: JSON.stringify({ source }),
  });

export const deleteConversation = (patientId: string, sessionId: string) =>
  fetchWithAuth<{ success: boolean; deletedMessageCount: number }>(
    `/conversations/${encodeURIComponent(patientId)}/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' }
  );

export const deletePatientConversations = (patientId: string) =>
  fetchWithAuth<{ success: boolean; deletedMessageCount: number }>(
    `/patients/${encodeURIComponent(patientId)}/conversations`,
    { method: 'DELETE' }
  );

// AI Usage API
export interface AIUsageStats {
  summary: {
    totalCalls: number;
    totalTokens: number;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    totalCacheReadTokens: number;
    totalCacheWriteTokens: number;
    avgResponseTimeMs: number;
    parseFailureRate: number;
    errorRate: number;
    estimatedCost: number;
  };
  byEndpoint: Array<{
    endpoint: string;
    calls: number;
    totalTokens: number;
    avgTokens: number;
    avgResponseTimeMs: number;
    parseFailureRate: number;
    errorRate: number;
  }>;
  byModel: Array<{
    model: string;
    calls: number;
    totalTokens: number;
    avgTokens: number;
  }>;
  daily: Array<{
    date: string;
    calls: number;
    totalTokens: number;
    avgResponseTimeMs: number;
    errors: number;
  }>;
  recentErrors: Array<{
    endpoint: string;
    error: string;
    createdAt: string;
  }>;
}

export async function getAIUsageStats(
  dateFrom?: string,
  dateTo?: string
): Promise<AIUsageStats> {
  const params = new URLSearchParams();
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  const queryString = params.toString();
  return fetchWithAuth<AIUsageStats>(`/ai-usage${queryString ? `?${queryString}` : ''}`);
}

// Access Code types
export interface AccessCode {
  id: number;
  code: string;
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: string | null;
  notes: string | null;
  clinicalSetting: string;
}

export interface ClinicalSettingOption {
  id: string;
  displayName: string;
  group: 'validated' | 'pediatric' | 'adult';
}

export interface AuditLogEntry {
  id: number;
  code: string;
  action: string;
  ipAddress: string | null;
  sessionId: string | null;
  createdAt: string;
  details: Record<string, unknown> | null;
}

// Access Code API
export async function getAccessCodes(): Promise<{ codes: AccessCode[] }> {
  return fetchWithAuth<{ codes: AccessCode[] }>('/access-codes');
}

export async function createAccessCode(
  code: string,
  clinicalSetting: string,
  expiresAt?: string,
  notes?: string
): Promise<AccessCode> {
  return fetchWithAuth<AccessCode>('/access-codes', {
    method: 'POST',
    body: JSON.stringify({ code, clinicalSetting, expiresAt, notes }),
  });
}

export async function revokeAccessCode(id: number): Promise<AccessCode> {
  return fetchWithAuth<AccessCode>(`/access-codes/${id}/revoke`, {
    method: 'PATCH',
  });
}

export async function getAccessCodeSuggestions(): Promise<{ suggestions: string[] }> {
  return fetchWithAuth<{ suggestions: string[] }>('/access-codes/suggest');
}

export async function getAccessCodeAuditLog(
  limit?: number,
  code?: string
): Promise<{ entries: AuditLogEntry[] }> {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  if (code) params.append('code', code);
  const queryString = params.toString();
  return fetchWithAuth<{ entries: AuditLogEntry[] }>(
    `/access-codes/audit${queryString ? `?${queryString}` : ''}`
  );
}

export async function getAccessCodeSettings(): Promise<{ settings: ClinicalSettingOption[] }> {
  return fetchWithAuth<{ settings: ClinicalSettingOption[] }>('/access-codes/settings');
}

// Agent Runs types (mirrors server/db/repositories/agent-trace-repo.ts row shapes, camelCase)
export interface TurnTrace {
  turn: number;
  inputTokens: number;
  outputTokens: number;
  modelMs: number;
  toolsMs: number;
  stopReason: string;
}

export interface ToolCallTrace {
  tool: string;
  args: Record<string, unknown>;
  resultTokensEst: number;
  durationMs: number;
  error?: string;
}

export interface AgentRunRow {
  id: string;
  chatMessageId: number | null;
  conversationId: string | null;
  sessionId: string | null;
  settingId: string | null;
  mode: 'agentic' | 'legacy';
  modelId: string;
  fellBack: boolean;
  fallbackReason: string | null;
  turns: TurnTrace[];
  toolCalls: ToolCallTrace[];
  inputTokens: number;
  outputTokens: number;
  baselineTokensEst: number | null;
  savingsPct: number | null;
  // Returned by the API; not currently rendered — kept as accurate response mirrors.
  baselineCostUsdEst: number | null;
  costSavingsPct: number | null;
  groundingScore: number | null;
  responseTimeMs: number;
  costUsdEst: number;
  createdAt: string;
}

export type AgentRunDetail = AgentRunRow;

export interface AgentAggregates {
  count: number;
  avgSavingsPct: number | null;
  // Returned by the API; not currently rendered — kept as accurate response mirrors.
  avgCostSavingsPct: number | null;
  avgInputTokens: number | null;
  avgGroundingScore: number | null;
  fallbackRate: number | null;
  toolUsage: Array<{ tool: string; uses: number }>;
}

// Agent Runs API
export const getAgentRuns = (params: URLSearchParams) =>
  fetchWithAuth<{ rows: AgentRunRow[]; total: number }>(`/agent/runs?${params}`);

export const getAgentRun = (id: string) =>
  fetchWithAuth<AgentRunDetail>(`/agent/runs/${id}`);

export const getAgentRunAggregate = (params: URLSearchParams) =>
  fetchWithAuth<AgentAggregates>(`/agent/runs/aggregate?${params}`);

export const clearAgentRuns = () => fetchWithAuth<void>(`/agent/runs`, { method: 'DELETE' });
export const getAgentMode = () =>
  fetchWithAuth<{ mode: 'legacy' | 'agentic'; modelId: string; provider: string }>(`/agent/mode`);

export const setAgentMode = (mode: 'legacy' | 'agentic') =>
  fetchWithAuth<void>(`/agent/mode`, { method: 'PUT', body: JSON.stringify({ mode }) });

// ============ Startup generation ============

export const getStartupGeneration = () =>
  fetchWithAuth<{ enabled: boolean }>(`/startup-generation`);

export const setStartupGeneration = (enabled: boolean) =>
  fetchWithAuth<void>(`/startup-generation`, { method: 'PUT', body: JSON.stringify({ enabled }) });

// ============ AI Model selection ============

export interface AiModelPricing {
  inputPerMTok: number;
  outputPerMTok: number;
}

export interface AvailableAiModel {
  id: string;
  displayName: string;
  provider: string;
  reasoning: boolean;
  /** Always true for a selectable model — see toolRoute for which transport it uses. */
  toolUse: boolean;
  /** Which agentic tool-use transport this model runs on: Converse (Anthropic) or the OpenAI-compat mantle endpoint. */
  toolRoute: 'converse' | 'mantle';
  contextWindowTokens: number;
  pricing: AiModelPricing | null;
}

export interface ExcludedAiModel {
  id: string;
  displayName: string;
  provider: string;
  reason: string;
  /** 'baa' entries are hidden entirely by the picker; all other categories stay visible, greyed out. */
  category: 'baa' | 'capability' | 'outage' | 'ambiguous';
}

export interface ActiveAiModel {
  modelId: string;
  source: 'admin-selected' | 'env-default';
  provider: string | null;
  displayName: string | null;
  selectedAt: string | null;
  verifiedMs: number | null;
  bedrockProvider: boolean;
  defaultModelId: string;
  defaultSource: 'env' | 'built-in';
}

export const getActiveAiModel = () => fetchWithAuth<ActiveAiModel>(`/ai-model`);

export const getAvailableAiModels = () =>
  fetchWithAuth<{ models: AvailableAiModel[]; excluded: ExcludedAiModel[] }>(`/ai-model/available`);

export const saveAiModel = (m: { modelId: string }) =>
  fetchWithAuth<{ ok: boolean; latencyMs: number }>(`/ai-model`, {
    method: 'PUT',
    body: JSON.stringify(m),
  });

export const resetAiModel = () =>
  fetchWithAuth<void>(`/ai-model`, { method: 'DELETE' });

